import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import {
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { InputComponent } from 'src/app/components/shared/input';
import { DropdownComponent } from '../../../../shared/dropdown';
import { TrashComponent } from '../../../../icons/trash';
import { PlusComponent } from '../../../../icons/plus';
import { LocaleService } from 'src/app/services/locale';
import { CategoryService } from 'src/app/services/category';

@Component({
  selector: 'app-category3-question5',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputComponent,
    DropdownComponent,
    TrashComponent,
    PlusComponent
  ],
  template: `
    <div>
      <div class="flex justify-between mb-4 items-center">
        <div class="text-[13px]">
          {{ strings[language].question }}
        </div>
        <div (click)="addRow()">
          <button
            class="bg-[#F8F9FA] px-3 py-2 rounded text-white text-md cursor-pointer"
          >
            <app-plus></app-plus>
          </button>
        </div>
      </div>

      <div class="space-y-4">
        <div
          *ngFor="let row of rows.controls; let i = index"
          class="border rounded-lg p-4"
        >
          <div class="flex justify-end mb-4 cursor-pointer">
            <div
              class="w-1/12 flex justify-end items-center cursor-pointer"
              (click)="removeRow(i)"
            >
              <app-trash></app-trash>
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium mb-1">
                {{ strings[language].label_1 }}
              </label>
              <app-dropdown
                placeholder="{{ strings[language].select }}"
                [options]="EmployeeGroupOptions"
                [control]="getControl(row, 'employeeGroup')"
              ></app-dropdown>
            </div>

            <div>
              <label class="block text-sm font-medium mb-1">
                {{ strings[language].label_2 }}
              </label>
              <app-input
                [placeholder]="strings[language].placeholder_departure"
                [control]="getControl(row, 'departure')"
              ></app-input>
            </div>

            <div>
              <label class="block text-sm font-medium mb-1">
                {{ strings[language].label_3 }}
              </label>
              <app-input
                [placeholder]="strings[language].placeholder_destination"
                [control]="getControl(row, 'destination')"
              ></app-input>
            </div>

            <div>
              <label class="block text-sm font-medium mb-1">
                {{ strings[language].label_4 }}
              </label>
              <app-dropdown
                placeholder="{{ strings[language].select }}"
                [options]="transportModeOptions"
                [control]="getControl(row, 'transportMode')"
              ></app-dropdown>
            </div>

            <!-- ✅ ONLY CHANGE: Month / Quarter added -->
            <div>
              <div class="flex flex-wrap gap-4 sm:gap-6 items-center mb-2">
                <label class="block text-sm font-medium">
                  {{ strings[language].label_5 }}
                </label>

                <div
                  class="flex gap-2 items-center cursor-pointer"
                  (click)="setFrequencyType(row, 'month')"
                >
                  <div
                    class="w-4 h-4 border rounded-full flex-shrink-0"
                    [ngClass]="{
                      'bg-[#183A25]':
                        getControl(row, 'frequencyType').value === 'month'
                    }"
                  ></div>
                  <div>{{ strings[language].month }}</div>
                </div>

                <div
                  class="flex gap-2 items-center cursor-pointer"
                  (click)="setFrequencyType(row, 'quarter')"
                >
                  <div
                    class="w-4 h-4 border rounded-full flex-shrink-0"
                    [ngClass]="{
                      'bg-[#183A25]':
                        getControl(row, 'frequencyType').value === 'quarter'
                    }"
                  ></div>
                  <div>{{ strings[language].quarter }}</div>
                </div>
              </div>

              <app-input
                type="number"
                [placeholder]="strings[language].placeholder_frequency"
                [control]="getControl(row, 'frequency')"
              ></app-input>
            </div>

            <div>
              <label class="block text-sm font-medium mb-1">
                {{ strings[language].label_6 }}
              </label>
              <app-input
                type="number"
                [placeholder]="strings[language].placeholder_roomnights"
                [control]="getControl(row, 'roomNights')"
              ></app-input>
            </div>

            <div>
              <label class="block text-sm font-medium mb-1">
                {{ strings[language].label_7 }}
              </label>
              <app-input
                [placeholder]="strings[language].placeholder_reduction"
                [control]="getControl(row, 'reductionMeasures')"
              ></app-input>
            </div>
          </div>
        </div>
      </div>
    </div>
  `
})
export class Category3Question5Component {
  private localeService = inject(LocaleService);
  private categoryService = inject(CategoryService);

  language = this.localeService.getLanguage();
  categories = this.categoryService.categories();

  form = new FormGroup({
    rows: new FormArray<FormGroup>([])
  });

  get rows(): FormArray<FormGroup> {
    return this.form.get('rows') as FormArray<FormGroup>;
  }

  employeeGroups = [
    { value: 'sales', label: 'Sales Team', labelIt: 'Squadra Vendite' },
    { value: 'management', label: 'Management Team', labelIt: 'Squadra Dirigenziale' },
    { value: 'marketing', label: 'Marketing Team', labelIt: 'Squadra Marketing' }
  ];

  EmployeeGroupOptions = this.employeeGroups.map(option => ({
    value: option.value,
    label: this.language === 'english' ? option.label : option.labelIt
  }));

  transportModes = [
    { value: 'flight', label: { en: 'Flight', it: 'Aereo' } },
    { value: 'train', label: { en: 'Train', it: 'Treno' } },
    { value: 'car', label: { en: 'Car', it: 'Macchina' } }
  ];

  private _transportModeCache: { lang: string; opts: { value: string; label: string }[] } | null = null;

  get transportModeOptions() {
    const lang = this.language;
    if (this._transportModeCache?.lang === lang) {
      return this._transportModeCache.opts;
    }
    const opts = this.transportModes.map(mode => ({
      value: mode.value,
      label: lang === 'italian' ? mode.label.it : mode.label.en
    }));
    this._transportModeCache = { lang, opts };
    return opts;
  }

  strings: any = {
    english: {
      question: 'Business Travel Details',
      label_1: 'Employee Group',
      label_2: 'Departure',
      label_3: 'Destination',
      label_4: 'Transport Mode',
      label_5: 'Frequency',
      label_6: 'Room-nights per Trip',
      label_7: 'Travel Reduction Measures',
      select: 'Select',
      month: 'month',
      quarter: 'quarter',
      placeholder_departure: 'New York',
      placeholder_destination: 'London',
      placeholder_frequency: '5',
      placeholder_roomnights: '3',
      placeholder_reduction: 'Remote meetings'
    },
    italian: {
      question: 'Dettagli sui Viaggi di Lavoro',
      label_1: 'Gruppo di Dipendenti',
      label_2: 'Partenza',
      label_3: 'Destinazione',
      label_4: 'Modalità di Trasporto',
      label_5: 'Frequenza',
      label_6: 'Notti in Camera per Viaggio',
      label_7: 'Misure di Riduzione dei Viaggi',
      select: 'Seleziona',
      month: 'mese',
      quarter: 'trimestre',
      placeholder_departure: 'New York',
      placeholder_destination: 'Londra',
      placeholder_frequency: '5',
      placeholder_roomnights: '3',
      placeholder_reduction: 'Riunioni da remoto'
    }
  };

  ngOnInit() {
    const saved = localStorage.getItem('categories');
    const parsed = saved ? JSON.parse(saved) : null;
    const answers = parsed?.[2]?.questions?.[4]?.answers || [];

    if (answers.length) {
      answers.forEach((a: any) => this.addRow(a));
    } else {
      this.addRow();
    }

    this.form.valueChanges.subscribe(() => this.saveForm());
  }

  getControl(row: FormGroup, controlName: string): FormControl {
    return row.get(controlName) as FormControl;
  }

  setFrequencyType(row: FormGroup, type: 'month' | 'quarter') {
    row.get('frequencyType')?.setValue(type);
  }

  createRow(data: any = {}): FormGroup {
    return new FormGroup({
      employeeGroup: new FormControl(data.employeeGroup || '', Validators.required),
      departure: new FormControl(data.departure || '', Validators.required),
      destination: new FormControl(data.destination || '', Validators.required),
      transportMode: new FormControl(
        data.transportMode?.value || data.transportMode || '',
        Validators.required
      ),
      frequency: new FormControl(data.frequency || '', Validators.required),

      // ✅ ONLY NEW FIELD
      frequencyType: new FormControl(data.frequencyType || 'month'),

      roomNights: new FormControl(data.roomNights || '', Validators.required),
      reductionMeasures: new FormControl(data.reductionMeasures || '')
    });
  }

  addRow(data?: any) {
    this.rows.push(this.createRow(data));
  }

  removeRow(index: number) {
    this.rows.removeAt(index);
    this.saveForm();
  }

  saveForm() {
    const newAnswers = this.rows.value.map((row: any) => {
      const selectedMode = this.transportModes.find(
        m => m.value === row.transportMode
      );
      return {
        ...row,
        transportMode: {
          value: selectedMode?.value,
          en: selectedMode?.label.en,
          it: selectedMode?.label.it
        }
      };
    });

    this.categories[2].questions[4].answers = newAnswers as any;
    this.categoryService.persistCategories(this.categories);
  }
}
